import React from "react";

export interface SkeletonProps {
  className?: string;
  "data-testid"?: string;
}

export const Skeleton: React.FC<SkeletonProps> = ({
  className = "h-4 w-full",
  "data-testid": testId = "skeleton-base",
}) => (
  <div
    data-testid={testId}
    className={`animate-pulse rounded bg-gray-200/80 ${className}`}
  />
);

/**
 * Skeleton for summary / KPI cards (e.g. Total Hospitals, Total Subscriptions)
 */
export const KpiCardSkeleton: React.FC<{ count?: number; className?: string }> = ({
  count = 5,
  className = "",
}) => (
  <div
    data-testid="kpi-skeleton-group"
    className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 ${className}`}
  >
    {Array.from({ length: count }).map((_, i) => (
      <div
        key={i}
        data-testid="kpi-card-skeleton"
        className="flex flex-col justify-between rounded-xl border border-[#E4EAF0] bg-white p-5 shadow-xs animate-pulse"
      >
        <div>
          <div className="flex items-start justify-between">
            <div className="space-y-2">
              <Skeleton className="h-3 w-20" />
              <Skeleton className="h-7 w-16" />
            </div>
            <Skeleton className="h-10 w-10 rounded-xl" />
          </div>
        </div>
        <div className="mt-4 border-t border-[#F0F4F8] pt-3 flex items-center gap-2">
          <Skeleton className="h-2 w-12" />
          <Skeleton className="h-2 w-12" />
          <Skeleton className="h-2 w-12" />
        </div>
      </div>
    ))}
  </div>
);

/**
 * Skeleton for line / bar chart containers
 */
export const ChartSkeleton: React.FC<{
  height?: number | string;
  hasHeader?: boolean;
  className?: string;
}> = ({ height = 240, hasHeader = true, className = "" }) => (
  <div
    data-testid="chart-skeleton"
    className={`rounded-xl border border-[#E4EAF0] bg-white p-5 shadow-xs animate-pulse space-y-4 ${className}`}
  >
    {hasHeader && (
      <div className="flex items-center justify-between pb-3 border-b border-[#F0F4F8]">
        <div className="space-y-1.5">
          <Skeleton className="h-4 w-36" />
          <Skeleton className="h-2.5 w-24" />
        </div>
        <Skeleton className="h-8 w-28 rounded-lg" />
      </div>
    )}

    <div
      style={{ height: typeof height === "number" ? `${height}px` : height }}
      className="flex items-end gap-3 pt-6 pb-2 px-2"
    >
      <div className="w-full h-full flex items-end justify-between gap-2">
        <Skeleton className="h-1/3 w-full rounded-t" />
        <Skeleton className="h-1/2 w-full rounded-t" />
        <Skeleton className="h-3/4 w-full rounded-t" />
        <Skeleton className="h-2/3 w-full rounded-t" />
        <Skeleton className="h-4/5 w-full rounded-t" />
        <Skeleton className="h-3/5 w-full rounded-t" />
      </div>
    </div>

    <div className="flex justify-between pt-1">
      <Skeleton className="h-2 w-10" />
      <Skeleton className="h-2 w-10" />
      <Skeleton className="h-2 w-10" />
      <Skeleton className="h-2 w-10" />
    </div>
  </div>
);

/**
 * Skeleton for Donut / Pie chart containers
 */
export const DonutSkeleton: React.FC<{ className?: string }> = ({ className = "" }) => (
  <div
    data-testid="donut-skeleton"
    className={`rounded-xl border border-[#E4EAF0] bg-white p-5 shadow-xs animate-pulse space-y-4 ${className}`}
  >
    <div className="pb-3 border-b border-[#F0F4F8] space-y-1.5">
      <Skeleton className="h-4 w-32" />
      <Skeleton className="h-2.5 w-44" />
    </div>

    <div className="flex flex-col items-center justify-center py-4">
      {/* Outer circular ring shimmer */}
      <div className="relative h-40 w-40 rounded-full border-[18px] border-gray-200/80 flex items-center justify-center">
        <div className="space-y-1 text-center">
          <Skeleton className="h-5 w-10 mx-auto" />
          <Skeleton className="h-2 w-12 mx-auto" />
        </div>
      </div>

      {/* Legend shimmer */}
      <div className="mt-5 w-full max-w-[240px] space-y-2 pt-3 border-t border-[#F0F4F8]">
        <div className="flex justify-between items-center">
          <Skeleton className="h-2.5 w-16" />
          <Skeleton className="h-2.5 w-8" />
        </div>
        <div className="flex justify-between items-center">
          <Skeleton className="h-2.5 w-20" />
          <Skeleton className="h-2.5 w-8" />
        </div>
        <div className="flex justify-between items-center">
          <Skeleton className="h-2.5 w-14" />
          <Skeleton className="h-2.5 w-8" />
        </div>
      </div>
    </div>
  </div>
);

/**
 * Skeleton for tabular data (e.g. Top Plans table)
 */
export const TableSkeleton: React.FC<{
  rows?: number;
  columns?: number;
  className?: string;
}> = ({ rows = 4, columns = 3, className = "" }) => (
  <div
    data-testid="table-skeleton"
    className={`rounded-xl border border-[#E4EAF0] bg-white p-5 shadow-xs animate-pulse space-y-3 ${className}`}
  >
    <div className="pb-3 border-b border-[#F0F4F8]">
      <Skeleton className="h-4 w-44" />
    </div>

    <div className="space-y-3 pt-1">
      {/* Table header */}
      <div className="flex justify-between pb-2 border-b border-[#F0F4F8]">
        {Array.from({ length: columns }).map((_, col) => (
          <Skeleton key={col} className="h-3 w-16" />
        ))}
      </div>

      {/* Rows */}
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="flex justify-between items-center py-2 border-b border-gray-100/60">
          <div className="flex items-center gap-2">
            <Skeleton className="h-3 w-3 rounded-full" />
            <Skeleton className="h-3 w-28" />
          </div>
          <Skeleton className="h-3 w-12" />
          <Skeleton className="h-3 w-16" />
        </div>
      ))}
    </div>
  </div>
);

/**
 * Skeleton for overview card (e.g. Hospital Overview, Doctor Overview)
 */
export const OverviewCardSkeleton: React.FC<{ className?: string }> = ({ className = "" }) => (
  <div
    data-testid="overview-card-skeleton"
    className={`bg-white rounded-lg shadow-xs border border-[#E4EAF0] overflow-hidden animate-pulse ${className}`}
  >
    <div className="px-6 py-4 border-b border-[#E4EAF0] bg-gray-50/30 flex items-center gap-3">
      <Skeleton className="h-5 w-5 rounded" />
      <Skeleton className="h-5 w-36" />
    </div>

    <div className="p-6 space-y-6">
      {/* 4-item Metrics strip shimmer */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 rounded-xl border border-[#E4EAF0] p-3 bg-gray-50/50">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="flex flex-col items-center justify-center py-2 space-y-2">
            <Skeleton className="h-3 w-14" />
            <Skeleton className="h-6 w-10" />
          </div>
        ))}
      </div>

      {/* Donut chart shimmer */}
      <div className="flex items-center justify-center py-4">
        <div className="h-36 w-36 rounded-full border-[16px] border-gray-200/80 flex items-center justify-center">
          <Skeleton className="h-4 w-12" />
        </div>
      </div>
    </div>
  </div>
);

export default Skeleton;
